/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { isPlatformServer } from "@angular/common";
import { Injectable, PLATFORM_ID, inject } from "@angular/core";
import {
	type ActivatedRouteSnapshot,
	type CanActivate,
	Router,
	type RouterStateSnapshot,
	type UrlTree,
} from "@angular/router";
import { AuthStateService } from "@app/@services/shared/auth-state.service";
import { AuthTenantService } from "@app/@services/shared/auth-tenant.service";
import type { Observable } from "rxjs";
import { CbRoutesUtils } from "../@classes/routes/cb-routes-utils";
import { AuthService } from "../@services/shared/auth.service";
import { ErrorHandlerService } from "../@services/shared/error-handler.service";
import { LoggerService } from "../@services/shared/logger.service";
@Injectable({
	providedIn: "root",
})
export class TenantAuthGuard implements CanActivate {
	private readonly selectTenantUrl = CbRoutesUtils.paths.tenant.path;
	private readonly router: Router = inject(Router);
	public errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
	public loggerService: LoggerService = inject(LoggerService);

	private readonly platformId = inject(PLATFORM_ID);
	private readonly authService = inject(AuthService);
	private readonly authTenantService = inject(AuthTenantService);
	private readonly authStateService = inject(AuthStateService);

	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot,
	):
		| Observable<boolean | UrlTree>
		| Promise<boolean | UrlTree>
		| boolean
		| UrlTree {
		this.loggerService.logInfo(
			"[TenantAuthGuard] Checking if tenant is selected",
		);

		const tenantId = this.authStateService.currentTenantId();

		if (isPlatformServer(this.platformId)) {
			return true;
		}

		if (tenantId == null || tenantId === undefined || tenantId === "") {
			this.loggerService.logWarning(
				"No tenant selected, navigating to tenant selection page",
			);
			this.router.navigate([this.selectTenantUrl]);
			return false;
		}

		return true;
	}
}
